import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'
import { AuthCard } from '@/components/auth/AuthCard'
import { AuthForm, Field } from '@/components/auth/AuthForm'
import { supabase } from '@/lib/supabase'

const MIN_PASSWORD_LENGTH = 8

// =============================================================================
// /reset-password  (public)
//
// Landing page for the password-recovery email link. The email carries a
// one-time token_hash in the query string (see
// supabase/templates/auth/reset-password.html). We deliberately DON'T verify
// it on page load — the token is exchanged only when the user submits a new
// password:
//
//   verifyOtp({ token_hash, type: 'recovery' })  → establishes a session
//   updateUser({ password })                     → sets the new password
//
// Consequences of verify-on-submit:
//   - A visitor who lands here but never completes the form is NOT signed in
//     (fixes the "reset link just logs you in" behaviour).
//   - An email scanner / link-preview bot that pre-fetches the link can't
//     burn the one-time token (a plain GET does nothing).
//   - On success the user is signed in and sent to /account, exactly like a
//     normal sign-in.
//
// The page is public (outside ProtectedRoute) and uses the minimal header so
// there's no nav/account menu to wander off into mid-reset.
// =============================================================================

const REQUEST_LINK_CLASSES = [
  'inline-flex w-full items-center justify-center gap-2',
  'px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
  'border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]',
  'text-[13px] text-[var(--color-accent)] font-medium',
  'hover:bg-[var(--color-accent-soft)]/80 transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-elev)]',
].join(' ')

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tokenHash = searchParams.get('token_hash')
  const type = searchParams.get('type')
  // A usable link carries a recovery token_hash. Anything else (missing token,
  // wrong type, tampered URL) short-circuits to the "request a new link" state.
  const hasValidLink = Boolean(tokenHash) && type === 'recovery'

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // True when the failure is with the token itself (expired / used / invalid),
  // so we surface the "request a new link" affordance alongside the error.
  const [linkError, setLinkError] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLinkError(false)
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    setSubmitting(true)

    // 1. Exchange the one-time recovery token for a session. This is the first
    //    point the token is used, so an expired/consumed link fails here.
    const { error: verifyError } = await supabase.auth.verifyOtp({
      type: 'recovery',
      token_hash: tokenHash as string,
    })
    if (verifyError) {
      setSubmitting(false)
      setLinkError(true)
      setError('This reset link is invalid or has expired. Request a fresh one below.')
      return
    }

    // 2. Set the new password. verifyOtp has now established a recovery
    //    session, so if the update FAILS (e.g. a password-policy rejection or a
    //    transient error) the user would otherwise be left signed in without
    //    having changed their password — exactly the "reset link just logs me
    //    in" behaviour this flow removes. Sign out so a failed reset never
    //    yields an authenticated session; the token is spent, so steer the user
    //    to request a fresh link.
    const { error: updateError } = await supabase.auth.updateUser({ password })
    if (updateError) {
      await supabase.auth.signOut()
      setSubmitting(false)
      setLinkError(true)
      setError(updateError.message)
      return
    }

    setSubmitting(false)
    // Signed in with the new password — land on /account like a normal login.
    navigate('/account', { replace: true })
  }

  return (
    <SiteLayout minimalHeader>
      <AuthCard
        title={hasValidLink ? 'Set a New Password' : 'Reset Link Problem'}
        subtitle={
          hasValidLink
            ? 'Choose a new password to finish. You’ll be signed in once it’s set.'
            : 'This password-reset link is invalid or incomplete.'
        }
        footer={
          <>
            Remembered it?{' '}
            <Link
              to="/sign-in"
              className="text-[var(--color-accent)] underline underline-offset-2"
            >
              Sign in
            </Link>
            .
          </>
        }
      >
        {hasValidLink ? (
          <>
            <AuthForm
              onSubmit={onSubmit}
              submitLabel="Set Password & Sign In"
              submitting={submitting}
              error={error}
            >
              <Field
                id="new-password"
                label="New password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                placeholder="At least 8 characters"
              />
              <Field
                id="confirm-password"
                label="Confirm new password"
                type="password"
                value={confirm}
                onChange={setConfirm}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
              />
            </AuthForm>
            {linkError ? (
              <div className="mt-4">
                <Link to="/forgot-password" className={REQUEST_LINK_CLASSES}>
                  Request a new link
                </Link>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col gap-4">
            <div
              role="alert"
              className={[
                'flex items-start gap-2.5',
                'rounded-[var(--radius-DEFAULT)] border border-[var(--color-signal)]/40',
                'bg-[var(--color-signal-soft)] px-3 py-2.5',
                'text-[12.5px] text-[var(--color-text)]',
              ].join(' ')}
            >
              <AlertTriangle
                size={14}
                className="text-[var(--color-signal)] shrink-0 mt-0.5"
                aria-hidden
              />
              <span>
                The link may have expired, already been used, or been altered.
                Request a fresh one below.
              </span>
            </div>
            <Link to="/forgot-password" className={REQUEST_LINK_CLASSES}>
              Request a new link
            </Link>
          </div>
        )}
      </AuthCard>
    </SiteLayout>
  )
}
